import { useEffect, useMemo, useState } from "react";

import { useUsers } from "@/apiQueries/useUsers";

import { useLeaveConfirmation } from "@/hooks/useLeaveConfirm";
import { useRedux } from "@/hooks/useRedux";

import { useEnrollmentKeySignFlow } from "./useEnrollmentKeySignFlow";

import type { ApiUser, MultisigEnrollmentSession } from "@/types";

export type InviteeFlowStep = "intro" | "qr" | "review" | "success";

type UseInviteeOwnerFlowParams = {
  orgName: string;
  activeSession?: MultisigEnrollmentSession | null;
  onBack: () => void;
  onDone: () => void;
};

const fullName = (u: ApiUser) => `${u.first_name} ${u.last_name}`.trim() || u.email;

export const useInviteeOwnerFlow = ({
  orgName,
  activeSession = null,
  onBack,
  onDone,
}: UseInviteeOwnerFlowParams) => {
  const { profile } = useRedux("profile");
  const usersQuery = useUsers();
  const keySignFlow = useEnrollmentKeySignFlow();
  const {
    keyResult,
    hasConnectedKey,
    signError,
    signRequestPending,
    disconnectWalletRef,
    signRef,
    submitKeyMutation,
    verifyKeyMutation,
    challengeXdr,
    handleKeyConnected,
    handleKeyDisconnected,
    signAndVerify,
    canProceed,
    isBusy,
  } = keySignFlow;

  const initialStep: InviteeFlowStep = useMemo(() => {
    const s = activeSession?.status;
    if (s === "READY" || s === "SUBMITTING" || s === "COMPLETED") return "success";
    return "intro";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentStep, setCurrentStep] = useState<InviteeFlowStep>(initialStep);

  const owner1User = useMemo<ApiUser | null>(() => {
    if (!activeSession?.initiator_user_id) return null;
    return usersQuery.data?.find((u) => u.id === activeSession.initiator_user_id) ?? null;
  }, [activeSession, usersQuery.data]);
  const owner1Name = owner1User ? fullName(owner1User) : "Owner 1";
  const owner1Email = owner1User?.email;

  const youName =
    profile.data.firstName || profile.data.lastName
      ? `${profile.data.firstName} ${profile.data.lastName}`.trim()
      : "";
  const youEmail = profile.data.email;

  const canConfirm = canProceed && !isBusy;

  // The exit dialog only makes sense once the user has connected their wallet
  // — before that, there's no in-progress state worth confirming away.
  const shouldBlock = hasConnectedKey && currentStep !== "success";
  const { isBlocked, confirmExit, cancelExit } = useLeaveConfirmation(shouldBlock);

  const handleConfirm = async () => {
    const ok = await signAndVerify();
    if (!ok) return;
    setCurrentStep("success");
  };

  const handleBack = () => {
    if (currentStep === "qr") setCurrentStep("intro");
    if (currentStep === "review") setCurrentStep("qr");
  };

  const goToQr = () => setCurrentStep("qr");
  const goToReview = () => setCurrentStep("review");

  const handleConfirmExit = () => {
    if (hasConnectedKey) {
      void disconnectWalletRef.current?.();
    }
    confirmExit();
  };

  const handleDone = () => {
    onDone();
  };

  useEffect(() => {
    if (!activeSession) return;
    if (
      currentStep !== "success" &&
      (activeSession.status === "CANCELLED" ||
        activeSession.status === "EXPIRED" ||
        activeSession.status === "FAILED")
    ) {
      void disconnectWalletRef.current?.();
    }
  }, [activeSession, currentStep, disconnectWalletRef]);

  const successStatus = activeSession?.status;
  const successHeading =
    successStatus === "COMPLETED"
      ? `Co-signing is on for ${orgName}.`
      : successStatus === "READY" || successStatus === "SUBMITTING"
        ? "Finalizing co-signing."
        : "Confirmed. Co-signing is enabled.";
  const successBody =
    successStatus === "READY" || successStatus === "SUBMITTING"
      ? `We're recording your co-signing key on the Stellar network — this usually takes a few seconds.`
      : `Co-signing is now on for ${orgName}. Every disbursement will need two co-signers before funds go out.`;

  return {
    // Step routing
    currentStep,
    goToQr,
    goToReview,
    handleBack,
    onBack,

    // Wallet key step
    keyResult,
    challengeXdr,
    hasConnectedKey,
    handleKeyConnected,
    handleKeyDisconnected,
    disconnectWalletRef,
    signRef,

    // Signing / confirm
    signRequestPending,
    signError,
    canConfirm,
    handleConfirm,

    // Exit / leave-confirmation
    isBlocked,
    cancelExit,
    handleConfirmExit,

    // Success / done
    handleDone,
    successHeading,
    successBody,

    // Identity for ReviewDetails
    owner1Name,
    owner1Email,
    youName,
    youEmail,

    // Mutation status (exposed wholesale so the view can drive its own banners)
    submitKeyMutation,
    verifyKeyMutation,
  };
};
